@if (data$ | async; as data) {
  @if (data.shouldPresentStripeMode) {
    <!-- Split Billing Setup Banner -->
    @if (isSplitBillingPending) {
      <div class="split-billing-setup-banner">
        <div class="split-billing-setup-header">
          <div class="split-billing-setup-icon">
            <pdq-icon icon="warning" color="var(--warning-default)" size="24"></pdq-icon>
          </div>
          <div class="split-billing-setup-info">
            <div class="split-billing-setup-title">Complete your plan setup</div>
            <div class="split-billing-setup-description">
              Your Shopify subscription has been approved. To activate your plan, please set up a payment method (credit
              card or ACH)
              @if (splitBillingStripeAmount) {
                for the Stripe portion <strong>(${{ splitBillingStripeAmount }}/period)</strong>
              }
              using the Payment Methods section below.
            </div>
          </div>
        </div>

        @if (splitBillingError) {
          <div class="split-billing-error">{{ splitBillingError }}</div>
        }
      </div>
    }

    <!-- Header Portal for Cancel/Save buttons when editing auto top-up -->
    @if (isEditingAutoTopUp) {
      <ng-template pdqHeaderContent>
        <div class="header__actions">
          <pdq-button variant="tertiary" (onClick)="cancelEditingAutoTopUp()"> Cancel </pdq-button>
          <pdq-button
            variant="primary"
            [loading]="isSavingAutoTopUp"
            [disabled]="isSavingAutoTopUp"
            (onClick)="saveAutoTopUpConfig()">
            Save
          </pdq-button>
        </div>
      </ng-template>
    }

    <div class="overview-container">
      <div class="overview-section">
        <div class="overview-box">
          <div class="overview-box-content">
            <div class="overview-box-content-title">
              PDQ plan
              @if (data?.isRedirected) {
                <span>(redirected plan)</span>
              }
            </div>
            <div class="overview-box-content-div">
              <div class="overview-box-content-main">{{ data?.planName ? data?.planName : 'Missing chosen plan' }}</div>
              <div class="overview-box-content-secondary">{{ data?.planPrice }}</div>
            </div>
          </div>
          @if (!data?.isRedirected) {
            <div>
              <pdq-button variant="primary" (onClick)="onGoToPlans()">Upgrade</pdq-button>
            </div>
          }
        </div>

        <div class="overview-box">
          <div class="overview-box-content">
            <div class="overview-box-content-title">Current billing cycle</div>
            <div class="overview-box-content-div">
              <div class="overview-box-content-main">
                {{
                  data?.startDate && data?.expirationDate
                    ? data.startDate + ' - ' + data.expirationDate
                    : 'Missing cycle data'
                }}
              </div>
              <div class="overview-box-content-secondary">{{ data?.periodDisplay }}</div>
            </div>
          </div>
        </div>
        <div class="overview-box">
          <div class="overview-box-content">
            <div class="overview-box-content-title">Additional charges for current cycle</div>
            <div class="overview-box-content-div">
              <div class="overview-box-content-main">
                {{ data?.periodOvercharges ? 'Overcharges: ' + data.periodOvercharges : '' }}
              </div>
              <div class="overview-box-content-main">
                {{ data?.periodShippings ? 'Shipping: ' + data.periodShippings : '' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="overview-section">
        <div class="overview-box">
          <div class="overview-box-content">
            <div class="overview-box-content-title">Orders shipped this month</div>
            <div class="overview-box-content-secondary">In current billing cycle</div>
            <div class="overview-box-content-div">
              <div class="overview-box-content-main">{{ data?.delivered }}</div>
            </div>
          </div>
        </div>

        <div class="overview-box">
          <div class="overview-box-content">
            <div class="overview-box-content-title">Value of orders cancelled</div>
            <div class="overview-box-content-secondary">Pending refund approval from delivery service</div>
            <div class="overview-box-content-div">
              <div class="overview-box-content-main">
                {{ data?.pendingCreditsSum ? data?.pendingCreditsSum : '$0' }}
              </div>
            </div>
          </div>
        </div>

        <div class="overview-box">
          <div class="overview-box-content">
            <div class="overview-box-content-title">Available credits</div>
            <div class="overview-box-content-secondary">
              Applied automatically to future ShipDamnQuick™ label purchases
            </div>
            <div class="overview-box-content-div">
              <div class="overview-box-content-main">
                {{ data?.availableCreditsSum ? data?.availableCreditsSum : '$0' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Methods Section -->
      <div class="payment-methods-section">
        <div class="section-header">
          <h3 class="section-title">Payment Methods</h3>
        </div>

        <div class="payment-methods-list">
          <!-- ACH Bank Account Section -->
          @if (enableACHPayments) {
            <div class="payment-method-box" [class.not-setup]="!data.achBankAccountData">
              <div class="payment-method-content">
                <div class="payment-method-details">
                  <div class="payment-method-primary">ACH</div>
                  <div class="payment-method-secondary">
                    {{
                      data.achBankAccountData
                        ? 'Bank Account (••••' + data.achBankAccountData.last4 + ')'
                        : 'Secure, direct bank to bank payments'
                    }}
                  </div>
                </div>
                <div class="payment-method-actions">
                  <pdq-button
                    [variant]="'primary'"
                    [disabled]="!data.isEnablePaymentMethodEditing"
                    (onClick)="data.achBankAccountData ? onUpdateACHPaymentMethod() : onAddACHPaymentMethod()">
                    {{ data.achBankAccountData ? 'Edit' : 'Setup' }}
                  </pdq-button>
                  @if (data.achBankAccountData) {
                    <pdq-button
                      variant="tertiary"
                      [disabled]="!data.isEnablePaymentMethodEditing || isDeletingAchPaymentMethod"
                      (onClick)="onDeleteACHPaymentMethod()">
                      {{ isDeletingAchPaymentMethod ? 'Removing...' : 'Remove' }}
                    </pdq-button>
                  }
                </div>
              </div>

              <!-- ACH Balance and Add Funds - Only show if ACH account exists -->
              @if (data.achBankAccountData) {
                <div class="ach-balance-funds-section">
                  <!-- Available Balance -->
                  <div class="balance-box">
                    <div class="balance-title">Available Balance</div>
                    <div class="balance-amount">{{ availableBalance }}</div>

                    <div class="balance-divider"></div>

                    <!-- Preloaded and Used amounts -->
                    <div class="balance-details">
                      <div class="balance-detail-item">
                        <div class="detail-section">
                          <span class="detail-label">Preloaded</span>
                          <span class="detail-amount">{{ preloadedAmount }}</span>
                        </div>
                        <div class="detail-section">
                          <span class="detail-label">Used</span>
                          <span class="detail-amount">{{ usedAmount }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Add Funds -->
                  <div class="add-funds-box">
                    <div class="add-funds-header">
                      <div class="add-funds-title">Add funds</div>
                      <div class="funding-type-switch">
                        <button (click)="setFundingType('manual')" [class.active]="fundingType === 'manual'">
                          Manually
                        </button>
                        <button (click)="setFundingType('auto')" [class.active]="fundingType === 'auto'">
                          Auto top up
                        </button>
                      </div>
                    </div>

                    <!-- Manual Funding -->
                    @if (fundingType === 'manual') {
                      <div class="add-funds-amount-section">
                        <div class="amount-title">Amount</div>
                        <div class="amount-input-row">
                          <pdq-input-arrows
                            type="number"
                            [formControl]="addFundsControl"
                            (keyup)="onAmountKeyup($event)"
                            [disabled]="isAddingFunds"
                            placeholder="0.00"
                            step="1"
                            precision="2"
                            [min]="0"
                            widthSize="fullwidth">
                          </pdq-input-arrows>
                          <pdq-button
                            variant="primary"
                            [disabled]="addFundsAmount <= 0 || isAddingFunds"
                            (onClick)="onAddFunds()">
                            {{ isAddingFunds ? 'Adding...' : 'Add Funds' }}
                          </pdq-button>
                        </div>
                      </div>
                    }

                    <!-- Auto Top-up -->
                    @if (fundingType === 'auto') {
                      <div class="auto-top-up-section">
                        <div class="auto-top-up-inputs">
                          <!-- Add Amount -->
                          <div class="auto-input-group">
                            <div class="amount-title">Add</div>
                            <pdq-input-arrows
                              type="number"
                              [formControl]="autoTopUpAmountControl"
                              (keyup)="onAutoTopUpAmountKeyup($event)"
                              [disabled]="isSavingAutoTopUp"
                              placeholder="0.00"
                              step="1"
                              precision="2"
                              [min]="0"
                              widthSize="fullwidth">
                            </pdq-input-arrows>
                          </div>

                          <!-- Threshold -->
                          <div class="auto-input-group">
                            <div class="amount-title">When Balance Falls Under</div>
                            <pdq-input-arrows
                              type="number"
                              [formControl]="autoTopUpThresholdControl"
                              (keyup)="onAutoTopUpThresholdKeyup($event)"
                              [disabled]="isSavingAutoTopUp"
                              placeholder="0.00"
                              step="1"
                              precision="2"
                              [min]="0"
                              widthSize="fullwidth">
                            </pdq-input-arrows>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          }

          <!-- Visa Payment Method Box -->
          <div class="payment-method-box" [class.not-setup]="!data.creditCardData">
            <div class="payment-method-content">
              <div class="payment-method-details">
                <div class="payment-method-primary">
                  {{
                    data.creditCardData
                      ? data.creditCardData.brand
                        ? data.creditCardData.brand + ' ending in ' + data.creditCardData.last4
                        : 'Card ending in ' + data.creditCardData.last4
                      : 'Visa'
                  }}
                </div>
                <div class="payment-method-secondary">
                  {{
                    data.creditCardData
                      ? 'Exp ' + data.creditCardData.expirationMonth + '/' + data.creditCardData.expirationYear
                      : 'Add a Visa credit card for billing'
                  }}
                </div>
              </div>
              <div class="payment-method-actions">
                <pdq-button
                  [variant]="'primary'"
                  [disabled]="!data.isEnablePaymentMethodEditing"
                  (onClick)="data.creditCardData ? onUpdatePaymentMethod() : onAddPaymentMethod()">
                  {{ data.creditCardData ? 'Edit' : 'Setup' }}
                </pdq-button>
                @if (data.creditCardData) {
                  <pdq-button
                    variant="tertiary"
                    [disabled]="!data.isEnablePaymentMethodEditing || isDeletingPaymentMethod"
                    (onClick)="onDeleteCardPaymentMethod()">
                    {{ isDeletingPaymentMethod ? 'Removing...' : 'Remove' }}
                  </pdq-button>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  }
  @if (!data.shouldPresentStripeMode) {
    <div class="fields-box">
      <div class="settings-row">
        <span class="details title"
          >Store plan
          @if (data?.isRedirected) {
            <span>(redirected plan)</span>
          }
        </span>
        <span class="details">{{ data?.planName }}</span>
        @if (!data?.isRedirected) {
          <a class="details link" (click)="onGoToPlans()">Upgrade plan</a>
        }
      </div>
      <div class="settings-row">
        <span class="details title">Auto renewal date</span>
        <span class="details">{{ data?.expirationDate }}</span>
      </div>
      <div class="settings-row">
        <span class="details title">Orders shipped this {{ data?.periodName }} (in current billing cycle)</span>
        <span class="details">{{ data?.delivered }}</span>
      </div>
      <div class="settings-row">
        <span class="details title">Value of orders cancelled (pending refund approval from delivery service)</span>
        <span class="details">{{ data?.pendingCreditsSum }}</span>
      </div>
      <div class="settings-row">
        <span class="details title"
          >Refund credit available (applied automatically to future ShipDamnQuick™ label purchases)
        </span>
        <span class="details">{{ data?.availableCreditsSum }}</span>
      </div>
      <!--<div class="settings-row">
          <div></div>
          <span class="title">Total shipments</span>
          <span class="progress-label">{{ data?.delivered + ' / ' + data?.includedDeliveries }}</span>
          <pdq-progress-bar class="bar" [value]="data?.usedPercents"></pdq-progress-bar>
        </div>-->
    </div>
  }
} @else {
  @if (!hasError) {
    <skeleton-billing-overview></skeleton-billing-overview>
  } @else {
    <div class="error-state">
      <p>Failed to load overview, Please try again.</p>
    </div>
  }
}

<ng-template #error>
  <div class="error-state">
    <p>Failed to load overview, Please try again.</p>
  </div>
</ng-template>
